import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, orderBy, query } from "firebase/firestore";
import { getPlanDetails } from "../utils/premiumHelpers";
import firebaseConfig from "../firebaseConfig";

export default function AdminRevenueAnalytics() {
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState("all"); // all, 30d, 90d, 365d

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const appId = firebaseConfig.appId;

      // Fetch payments
      const paymentQuery = query(
        collection(db, `artifacts/${appId}/public/data/subscription_payments`),
        orderBy("createdAt", "desc")
      );
      const paymentSnapshot = await getDocs(paymentQuery);
      const paymentsList = paymentSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPayments(paymentsList);

      // Fetch subscriptions
      const subQuery = query(
        collection(db, `artifacts/${appId}/public/data/user_profiles`),
        orderBy("subscriptionStartDate", "desc")
      );
      const subSnapshot = await getDocs(subQuery);
      const subsList = subSnapshot.docs
        .filter((doc) => doc.data().subscriptionStatus)
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      setSubscriptions(subsList);
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    let filtered = payments;

    if (timeRange !== "all") {
      const now = new Date();
      let cutoffDate = new Date();

      if (timeRange === "30d") cutoffDate.setDate(now.getDate() - 30);
      else if (timeRange === "90d") cutoffDate.setDate(now.getDate() - 90);
      else if (timeRange === "365d") cutoffDate.setFullYear(now.getFullYear() - 1);

      filtered = filtered.filter((p) => {
        const payDate = p.createdAt?.toDate?.() || new Date(p.createdAt);
        return payDate >= cutoffDate;
      });
    }

    return filtered;
  };

  const filteredPayments = getFilteredData();

  // Calculate metrics
  const successPayments = filteredPayments.filter((p) => p.status === "success");
  const failedPayments = filteredPayments.filter((p) => p.status === "failed");
  const totalRevenue = successPayments.reduce((sum, p) => sum + (p.amountPaid || 0), 0);
  const avgTransaction = successPayments.length > 0 ? totalRevenue / successPayments.length : 0;

  // Revenue by plan
  const revenueByPlan = {};
  const paymentsByPlan = {};
  successPayments.forEach((p) => {
    revenueByPlan[p.plan] = (revenueByPlan[p.plan] || 0) + (p.amountPaid || 0);
    paymentsByPlan[p.plan] = (paymentsByPlan[p.plan] || 0) + 1;
  });

  // Active subscriptions by plan
  const activeSubsByPlan = {};
  subscriptions.filter((s) => s.subscriptionStatus === "active").forEach((s) => {
    activeSubsByPlan[s.subscriptionPlan] = (activeSubsByPlan[s.subscriptionPlan] || 0) + 1;
  });

  // Churn calculation
  const expiredSubs = subscriptions.filter((s) => s.subscriptionStatus === "expired").length;
  const churnRate =
    subscriptions.length > 0 ? ((expiredSubs / subscriptions.length) * 100).toFixed(1) : 0;

  // Monthly trend
  const monthlyRevenue = {};
  successPayments.forEach((p) => {
    const date = p.createdAt?.toDate?.() || new Date(p.createdAt);
    const month = date.toLocaleString("en-GB", { year: "numeric", month: "short" });
    monthlyRevenue[month] = (monthlyRevenue[month] || 0) + (p.amountPaid || 0);
  });

  if (loading) return <div className="text-center py-8">Loading analytics...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      {/* Time Range Filter */}
      <div className="bg-white dark:bg-gray-700 p-4 rounded-lg shadow">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Time Range
        </label>
        <div className="flex gap-2 flex-wrap">
          {["all", "30d", "90d", "365d"].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded font-semibold transition ${
                timeRange === range
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-500"
              }`}
            >
              {range === "all" ? "All Time" : range === "30d" ? "Last 30 Days" : range === "90d" ? "Last 90 Days" : "Last Year"}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm text-green-600 dark:text-green-400 font-semibold">Total Revenue</p>
          <p className="text-3xl font-bold text-green-800 dark:text-green-300 mt-2">
            ₹{totalRevenue.toLocaleString()}
          </p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-2">{successPayments.length} successful payments</p>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold">Avg Transaction</p>
          <p className="text-3xl font-bold text-blue-800 dark:text-blue-300 mt-2">
            ₹{avgTransaction.toFixed(0).toLocaleString()}
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">Per successful payment</p>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <p className="text-sm text-purple-600 dark:text-purple-400 font-semibold">Active Subscriptions</p>
          <p className="text-3xl font-bold text-purple-800 dark:text-purple-300 mt-2">
            {subscriptions.filter((s) => s.subscriptionStatus === "active").length}
          </p>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-2">
            {churnRate}% churn rate
          </p>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400 font-semibold">Failed Payments</p>
          <p className="text-3xl font-bold text-red-800 dark:text-red-300 mt-2">{failedPayments.length}</p>
          <p className="text-xs text-red-600 dark:text-red-400 mt-2">
            {failedPayments.length > 0 && successPayments.length > 0
              ? ((failedPayments.length / (successPayments.length + failedPayments.length)) * 100).toFixed(1)
              : 0}
            % failure rate
          </p>
        </div>
      </div>

      {/* Revenue by Plan */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Revenue by Plan</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(revenueByPlan).map(([plan, revenue]) => {
            const planDetails = getPlanDetails(plan);
            const count = paymentsByPlan[plan] || 0;

            return (
              <div key={plan} className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                <p className="font-semibold text-gray-800 dark:text-white">{planDetails?.name || plan}</p>
                <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-2">
                  ₹{revenue.toLocaleString()}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{count} transactions</p>
                {activeSubsByPlan[plan] && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                    {activeSubsByPlan[plan]} active
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Monthly Revenue Trend</h3>
        <div className="space-y-2">
          {Object.entries(monthlyRevenue)
            .sort()
            .reverse()
            .slice(0, 12)
            .map(([month, revenue]) => {
              const maxRevenue = Math.max(...Object.values(monthlyRevenue));
              const percentage = (revenue / maxRevenue) * 100;

              return (
                <div key={month} className="flex items-center gap-4">
                  <div className="w-20 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {month}
                  </div>
                  <div className="flex-1">
                    <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-6 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full flex items-center justify-end pr-2 transition-all"
                        style={{ width: `${percentage}%` }}
                      >
                        {percentage > 20 && (
                          <span className="text-xs font-bold text-white">₹{(revenue / 1000).toFixed(0)}K</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="w-24 text-right font-semibold text-gray-800 dark:text-white">
                    ₹{revenue.toLocaleString()}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow p-6">
        <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Total Users</p>
            <p className="text-2xl font-bold text-gray-800 dark:text-white mt-2">{subscriptions.length}</p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Success Rate</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-2">
              {successPayments.length + failedPayments.length > 0
                ? ((successPayments.length / (successPayments.length + failedPayments.length)) * 100).toFixed(1)
                : 0}
              %
            </p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Retention Rate</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-2">
              {(100 - churnRate).toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Avg Plan Duration</p>
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-2">
              {(() => {
                const durations = Object.keys(paymentsByPlan).map((plan) => {
                  const details = getPlanDetails(plan);
                  return (details?.durationMonths || 0) * (paymentsByPlan[plan] || 0);
                });
                const total = durations.reduce((a, b) => a + b, 0);
                return successPayments.length > 0 ? (total / successPayments.length).toFixed(1) : 0;
              })()}
              m
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
